import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalLocation } from '@/contexts/LocationContext';
import { useToast } from '@/hooks/use-toast';
import {
  Activity, Users, MapPin, Calendar, AlertCircle, CheckCircle2, Sparkles,
  RefreshCw, ChevronRight, Briefcase, Loader2, Building2, Truck, ClipboardList, FileSignature,
  FileText, AlertOctagon, AlertTriangle, ShieldCheck, ShieldAlert, Euro,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const PK_TONE = {
  gold: 'bg-yellow-100 text-yellow-800',
  green: 'bg-emerald-100 text-emerald-700',
  orange: 'bg-orange-100 text-orange-700',
  red: 'bg-rose-100 text-rose-700',
  unknown: 'bg-slate-100 text-slate-500',
};

function fmtEUR(n) {
  return `€${Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function LoonkostenWeekWidget({ rollup, week }) {
  if (!rollup) return null;
  const pk = rollup.pk || {};
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="dashboard-loonkosten-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Euro size={16} /> Loonkosten deze week
        </h3>
        <span className="text-[11px] text-slate-400">week {week}</span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
        <span className="text-2xl font-bold text-brand-navy tabular-nums" data-testid="loonkosten-week-total">
          {fmtEUR(rollup.total_cost_eur)}
        </span>
        <span className="text-xs text-slate-500">geplande loonkosten (alle gescopte locaties)</span>
        {pk.pk_pct != null && (
          <span className={`ml-auto rounded-full px-2.5 py-1 text-xs font-medium ${PK_TONE[pk.status] || PK_TONE.unknown}`} data-testid="loonkosten-week-pk">
            PK% {pk.pk_pct}%
          </span>
        )}
      </div>
      {rollup.top3 && rollup.top3.length > 0 ? (
        <div className="space-y-1.5" data-testid="loonkosten-top3">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Top-3 duurste locaties</p>
          {rollup.top3.map((r) => (
            <Link
              key={r.slug}
              to={`/locaties/${r.slug}`}
              data-testid={`loonkosten-top-${r.slug}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 hover:border-brand-amber hover:shadow-sm transition-all"
            >
              <span className="text-sm font-medium text-slate-800 truncate">{r.name}</span>
              <span className="flex items-center gap-2">
                <span className="text-sm tabular-nums text-slate-700">{fmtEUR(r.planned_cost_eur)}</span>
                {r.pk_pct != null && (
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${PK_TONE[r.status] || PK_TONE.unknown}`}>
                    {r.pk_pct}%
                  </span>
                )}
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500" data-testid="loonkosten-empty">Geen geplande loonkosten deze week.</p>
      )}
    </div>
  );
}

const STATUS_META = {
  green: { label: 'Volledig', dot: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  yellow: { label: 'Aandacht', dot: 'bg-amber-500', ring: 'ring-amber-200', text: 'text-amber-700', bg: 'bg-amber-50' },
  red: { label: 'Krap', dot: 'bg-rose-500', ring: 'ring-rose-200', text: 'text-rose-700', bg: 'bg-rose-50' },
  gray: { label: 'Geen data', dot: 'bg-slate-300', ring: 'ring-slate-200', text: 'text-slate-500', bg: 'bg-slate-50' },
};

import { KpiCard } from '@/components/ui/KpiCard';

function DataHealthWidget({ health }) {
  const [open, setOpen] = useState(false);
  if (!health) return null;
  const total = health.total_orphans ?? 0;
  const healthy = total === 0;
  const rows = [
    ...(health.collections || []).map((c) => ({
      key: c.collection,
      label: c.collection,
      count: c.orphan_count,
      samples: c.sample_ids || [],
      values: c.orphan_values || [],
    })),
    ...(health.employees
      ? [{
          key: 'employees',
          label: 'employees (home_location_id)',
          count: health.employees.orphan_count,
          samples: (health.employees.samples || []).map((s) => s.id),
          values: health.employees.orphan_values || [],
        }]
      : []),
  ];

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="dashboard-data-health-widget"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          {healthy ? (
            <ShieldCheck size={16} className="text-emerald-600" />
          ) : (
            <ShieldAlert size={16} className="text-rose-600" />
          )}
          Datakwaliteit
        </h3>
        {!healthy && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="data-health-toggle"
            className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-brand-navy"
          >
            {open ? 'Verberg detail' : 'Toon detail'}
            <ChevronRight size={11} className={open ? 'rotate-90 transition-transform' : 'transition-transform'} />
          </button>
        )}
      </div>

      {healthy ? (
        <p className="mt-2 text-sm text-emerald-700" data-testid="data-health-ok">
          Alles in orde ✓ — alle locatieverwijzingen kloppen ({health.locations_checked} locaties gecontroleerd).
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm font-medium text-rose-700" data-testid="data-health-orphan-total">
            {total} verweesde locatieverwijzing{total === 1 ? '' : 'en'} gevonden
            {' '}in {rows.length} {rows.length === 1 ? 'bron' : 'bronnen'}.
          </p>
          {open && (
            <div className="mt-3 space-y-2" data-testid="data-health-breakdown">
              {rows.map((r) => (
                <div
                  key={r.key}
                  data-testid={`data-health-row-${r.key}`}
                  className="rounded-lg border border-rose-100 bg-rose-50/60 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-semibold text-slate-800">{r.label}</span>
                    <Badge variant="destructive" className="tabular-nums">{r.count}</Badge>
                  </div>
                  {r.values.length > 0 && (
                    <p className="mt-1 text-[11px] text-slate-500">
                      Onbekende locatie(s): <span className="font-mono">{r.values.join(', ')}</span>
                    </p>
                  )}
                  {r.samples.length > 0 && (
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      Voorbeeld-id's: <span className="font-mono">{r.samples.join(', ')}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function WervingWidget({ funnel, scope }) {
  const f = funnel || { open_vacatures: 0, nieuwe_sollicitaties: 0, aangenomen_deze_maand: 0 };
  const items = [
    { key: 'open', label: 'Open vacatures', value: f.open_vacatures, to: `/vacatures?loc=${scope}`, accent: 'text-brand-navy' },
    { key: 'nieuw', label: 'Nieuwe sollicitaties', value: f.nieuwe_sollicitaties, to: `/vacatures?loc=${scope}`, accent: 'text-brand-amber-dark' },
    { key: 'aangenomen', label: 'Aangenomen deze maand', value: f.aangenomen_deze_maand, to: null, accent: 'text-emerald-600' },
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="dashboard-werving-widget">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Briefcase size={16} /> Werving
        </h3>
        <Link
          to={`/vacatures?loc=${scope}`}
          data-testid="dashboard-werving-link"
          className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-brand-navy"
        >
          Beheren <ChevronRight size={11} />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {items.map((it) => {
          const inner = (
            <>
              <p className={`text-2xl font-bold tabular-nums ${it.accent}`} data-testid={`werving-${it.key}-value`}>{it.value}</p>
              <p className="mt-1 text-[11px] leading-tight text-slate-500">{it.label}</p>
            </>
          );
          return it.to ? (
            <Link
              key={it.key}
              to={it.to}
              data-testid={`werving-${it.key}-tile`}
              className="rounded-lg border border-slate-200 p-3 transition-all hover:border-brand-amber hover:shadow-md"
            >
              {inner}
            </Link>
          ) : (
            <div key={it.key} data-testid={`werving-${it.key}-tile`} className="rounded-lg border border-slate-200 p-3">
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContractSignaleringWidget({ data }) {
  if (!data || (data.verlopen + data.bijna) === 0) return null;
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm border-l-[3px] border-l-red-400"
      data-testid="dashboard-contract-widget"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <FileSignature size={16} /> Contractvernieuwing nodig
        </h3>
        <Link
          to="/medewerkers?contract=beide&loc=all"
          data-testid="dashboard-contract-link"
          className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-brand-navy"
        >
          Bekijk medewerkers <ChevronRight size={11} />
        </Link>
      </div>
      <p className="text-sm text-slate-700" data-testid="contract-widget-summary">
        <span className="font-semibold text-red-700">{data.verlopen} verlopen</span>
        <span className="text-slate-400"> · </span>
        <span className="font-semibold text-amber-700">{data.bijna} lopen ≤30 dagen af</span>
      </p>
      <div className="mt-3 space-y-1">
        {data.per_location.map((r) => (
          <Link
            key={r.location_slug || r.location_name}
            to={`/medewerkers?contract=beide&loc=${r.location_slug || 'all'}`}
            data-testid={`contract-loc-${r.location_slug || 'geen'}`}
            className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-xs transition-all hover:border-brand-amber hover:shadow-sm"
          >
            <span className="text-slate-700">{r.location_name}</span>
            <span className="tabular-nums text-slate-500">
              {r.verlopen > 0 && <span className="text-red-700">{r.verlopen} verlopen</span>}
              {r.verlopen > 0 && r.bijna > 0 && ' · '}
              {r.bijna > 0 && <span className="text-amber-700">{r.bijna} ≤30 dgn</span>}
            </span>
          </Link>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        Informatief: een verlopen contract zegt niets over in/uit dienst of inzetbaarheid.
      </p>
    </div>
  );
}

function DocumentenWidget({ docs }) {
  if (!docs || (docs.count ?? 0) === 0) {
    return (
      <div
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
        data-testid="dashboard-documents-widget"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <FileText size={16} /> Documenten attentiepunten
          </h3>
          <Link
            to="/instellingen?loc=all"
            className="text-[11px] text-slate-500 hover:text-brand-navy inline-flex items-center gap-1"
            data-testid="dashboard-documents-link"
          >
            Beheren <ChevronRight size={11} />
          </Link>
        </div>
        <p className="text-sm text-slate-500" data-testid="dashboard-documents-empty">
          Geen documenten verlopen binnen 30 dagen. ✓
        </p>
      </div>
    );
  }

  const b = docs.buckets || { expired: [], red: [], orange: [] };
  const counts = {
    expired: b.expired?.length || 0,
    red: b.red?.length || 0,
    orange: b.orange?.length || 0,
  };
  const top = [...b.expired, ...b.red, ...b.orange].slice(0, 5);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="dashboard-documents-widget"
    >
      <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <FileText size={16} /> Documenten attentiepunten
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
            {docs.count} totaal
          </span>
        </h3>
        <Link
          to="/instellingen?tab=documenten&loc=all"
          data-testid="dashboard-documents-link"
          className="text-xs text-brand-navy hover:text-brand-amber-dark inline-flex items-center gap-1"
        >
          Volledig overzicht <ChevronRight size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div
          data-testid="dashboard-docs-expired"
          className="rounded-lg border border-rose-200 bg-rose-100/60 p-2 text-center"
        >
          <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-rose-800">
            <AlertOctagon size={10} /> Verlopen
          </div>
          <div className="text-xl font-bold tabular-nums text-rose-800">{counts.expired}</div>
        </div>
        <div
          data-testid="dashboard-docs-red"
          className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-center"
        >
          <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-rose-700">
            <AlertOctagon size={10} /> &lt; 7d
          </div>
          <div className="text-xl font-bold tabular-nums text-rose-700">{counts.red}</div>
        </div>
        <div
          data-testid="dashboard-docs-orange"
          className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-center"
        >
          <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-amber-800">
            <AlertTriangle size={10} /> &lt; 30d
          </div>
          <div className="text-xl font-bold tabular-nums text-amber-800">{counts.orange}</div>
        </div>
      </div>

      <div className="space-y-1.5">
        {top.map((d) => (
          <Link
            key={d.id}
            to={`/medewerkers/${d.employee_id}?tab=documenten&loc=all`}
            data-testid={`dashboard-doc-item-${d.id}`}
            className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-1.5 hover:border-brand-amber hover:bg-amber-50/30 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate">
                {d.employee_name || 'Onbekend'}
                <span className="ml-1.5 text-[11px] font-normal text-slate-500">
                  · {d.category_label}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 truncate">{d.title}</div>
            </div>
            <div className="shrink-0">
              <span
                className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium border ${
                  d.expiry_status === 'expired'
                    ? 'bg-rose-100 text-rose-800 border-rose-300'
                    : d.expiry_status === 'red'
                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                    : 'bg-amber-50 text-amber-800 border-amber-200'
                }`}
              >
                {d.expiry_status === 'expired'
                  ? `verlopen ${d.days_until_expiry !== null ? `(${Math.abs(d.days_until_expiry)}d)` : ''}`
                  : `nog ${d.days_until_expiry}d`}
              </span>
            </div>
          </Link>
        ))}
        {docs.count > top.length && (
          <Link
            to="/instellingen?tab=documenten&loc=all"
            className="block text-center text-xs text-slate-500 hover:text-brand-navy pt-1"
          >
            + {docs.count - top.length} meer →
          </Link>
        )}
      </div>
    </div>
  );
}


function StaffingTile({ loc }) {
  const meta = STATUS_META[loc.status] || STATUS_META.gray;
  
  // Map status to subtle left accent
  let borderAccent = 'border-l-tw1-brand-navy';
  if (loc.status === 'red') borderAccent = 'border-l-tw1-status-danger';
  if (loc.status === 'yellow') borderAccent = 'border-l-tw1-accent-gold';
  if (loc.status === 'gray') borderAccent = 'border-l-slate-300';
  
  return (
    <Link
      to={`/rooster?loc=${loc.slug}`}
      data-testid={`staffing-tile-${loc.slug}`}
      className={`group rounded-lg border border-[#e4e9f0] bg-white p-3 shadow-sm hover:shadow-md hover:border-tw1-brand-navy/30 transition-all border-l-[3px] ${borderAccent}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${meta.dot} ring-2 ${meta.ring}`} />
            <h4 className="text-sm font-semibold text-slate-900 truncate">{loc.name}</h4>
          </div>
          <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">{loc.type || 'locatie'}</p>
        </div>
        <Badge variant="outline" className={`text-[10px] ${meta.bg} ${meta.text} border-transparent`}>
          {meta.label}
        </Badge>
      </div>
      <div className="space-y-1.5">
        <div className="flex justify-between text-[11px]">
          <span className="text-slate-500">Bezetting</span>
          <span className={`font-medium tabular-nums ${meta.text}`}>
            {loc.coverage_pct !== null ? `${loc.coverage_pct}%` : '—'}
          </span>
        </div>
        {loc.coverage_pct !== null && (
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${meta.dot}`}
              style={{ width: `${Math.min(loc.coverage_pct, 100)}%` }}
            />
          </div>
        )}
        <div className="flex justify-between text-[11px] text-slate-500 pt-1">
          <span>{loc.planned_hours}h / {loc.contract_hours}h</span>
          <span>{loc.employees_active} medew.</span>
        </div>
        {loc.rejected_count > 0 && (
          <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700 text-[10px]">
            {loc.rejected_count} no-show
          </Badge>
        )}
      </div>
    </Link>
  );
}

function WeekstartWidget({ items }) {
  return (
    <div className="rounded-xl bg-tw1-brand-navy p-5 shadow-sm" data-testid="dashboard-weekstart-widget">
      <div className="mb-3 flex items-center gap-2">
        <Calendar size={16} className="text-tw1-accent-gold" />
        <h3 className="text-sm font-semibold text-white">Deze week — in één oogopslag</h3>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <Link
              key={it.key}
              to={it.to}
              data-testid={`weekstart-${it.key}`}
              className="group rounded-lg border border-white/10 bg-white/[0.05] p-4 transition-all hover:border-tw1-accent-gold/60 hover:bg-white/[0.09]"
            >
              <div className="flex items-center justify-between">
                <p
                  className={`text-3xl font-bold tabular-nums ${it.alert ? 'text-tw1-accent-gold' : 'text-white'}`}
                  data-testid={`weekstart-${it.key}-value`}
                >
                  {it.value}
                </p>
                <Icon size={18} className="text-white/50 transition-colors group-hover:text-tw1-accent-gold" />
              </div>
              <p className="mt-1 text-[12px] font-medium text-white/80">{it.label}</p>
              {it.sublabel && <p className="text-[11px] text-white/45">{it.sublabel}</p>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { globalLocation } = useGlobalLocation();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [briefingBusy, setBriefingBusy] = useState(false);
  const [filter, setFilter] = useState('all'); // all|red|yellow

  const isOwnerAdmin = user && (user.role === 'owner' || user.role === 'admin');

  const load = useCallback(async (forceBriefing = false) => {
    setLoading(true);
    try {
      if (forceBriefing && isOwnerAdmin) {
        await api.post('/dashboard/briefing/regenerate');
      }
      const [dashResp, docsResp] = await Promise.all([
        api.get('/dashboard/full'),
        api.get('/documents/expiring', { params: { days: 30 } }).catch(() => ({ data: null })),
      ]);
      setData({ ...dashResp.data, documents: docsResp.data });
    } catch (e) {
      toast({ title: 'Dashboard laden mislukt', variant: 'destructive' });
    } finally {
      setLoading(false);
      setBriefingBusy(false);
    }
  }, [toast, isOwnerAdmin]);

  useEffect(() => { load(); }, [load]);

  const [funnel, setFunnel] = useState(null);
  useEffect(() => {
    const params = globalLocation && globalLocation !== 'all' ? { location_slug: globalLocation } : {};
    api.get('/vacatures/funnel', { params })
      .then((r) => setFunnel(r.data))
      .catch(() => setFunnel(null));
  }, [globalLocation]);

  const [contractSig, setContractSig] = useState(null);
  useEffect(() => {
    api.get('/employees/contract-signalering')
      .then((r) => setContractSig(r.data))
      .catch(() => setContractSig(null));
  }, []);

  const [dataHealth, setDataHealth] = useState(null);
  useEffect(() => {
    if (!isOwnerAdmin) return;
    api.get('/data-health')
      .then((r) => setDataHealth(r.data))
      .catch(() => setDataHealth(null));
  }, [isOwnerAdmin]);

  const [budgetRollup, setBudgetRollup] = useState(null);
  const [costRollup, setCostRollup] = useState(null);
  useEffect(() => {
    if (!isOwnerAdmin || !data?.iso_week || !data?.iso_year) return;
    api.get('/loonkosten/rollup', { params: { year: data.iso_year, week: data.iso_week } })
      .then((r) => setCostRollup(r.data))
      .catch(() => setCostRollup(null));
  }, [isOwnerAdmin, data?.iso_week, data?.iso_year]);
  useEffect(() => {
    if (!data?.iso_week || !data?.iso_year) return;
    api.get('/roster-guard/overview', { params: { year: data.iso_year, week: data.iso_week } })
      .then((r) => {
        let locs = r.data.locations || [];
        if (globalLocation && globalLocation !== 'all') locs = locs.filter((l) => l.location_slug === globalLocation);
        const over = locs.filter((l) => l.budget?.status === 'over');
        const under = locs.filter((l) => l.budget?.status === 'under');
        setBudgetRollup({
          over: over.length,
          under: under.length,
          firstSlug: (over[0] || under[0])?.location_slug || 'all',
        });
      })
      .catch(() => setBudgetRollup(null));
  }, [data?.iso_week, data?.iso_year, globalLocation]);

  const regenerateBriefing = async () => {
    setBriefingBusy(true);
    await load(true);
    toast({ title: 'AI-briefing vernieuwd' });
  };

  const filteredStaffing = useMemo(() => {
    if (!data?.staffing) return [];
    
    // First apply global scope
    let locations = data.staffing;
    if (globalLocation !== 'all') {
      locations = locations.filter((s) => s.slug === globalLocation);
    }
    
    // Then apply local status filter
    if (filter === 'red') return locations.filter((s) => s.status === 'red');
    if (filter === 'yellow') return locations.filter((s) => s.status === 'yellow');
    if (filter === 'green') return locations.filter((s) => s.status === 'green');
    return locations.filter((s) => s.status !== 'gray' || globalLocation !== 'all');
  }, [data, filter, globalLocation]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500" data-testid="page-dashboard-loading">
        <Loader2 size={20} className="animate-spin mr-2" /> Dashboard laden…
      </div>
    );
  }

  // Role-aware routing: single_location users should land on their specific cockpit
  if (user?.role === 'single_location' && user.locations?.length > 0) {
    return <Navigate to={`/locaties/${user.locations[0]}`} replace />;
  }

  if (!data) return null;

  const k = data.kpis;
  // Deep-link targets for single-location pages: the location that actually HAS
  // the pending work (so actions never dump the owner on an empty default loc).
  const staffingList = data.staffing || [];
  const primaryTarget = staffingList[0]?.slug || 'all';
  const greetingTime = new Date().getHours();
  const greeting = greetingTime < 12 ? 'Goedemorgen' : greetingTime < 18 ? 'Goedemiddag' : 'Goedenavond';
  const firstName = user?.email?.split('@')[0]?.split('.')[0] || 'collega';
  const firstNameCap = firstName.charAt(0).toUpperCase() + firstName.slice(1);

  const scope = globalLocation && globalLocation !== 'all' ? globalLocation : 'all';
  const krappe = (k.red_locations || 0) + (k.yellow_locations || 0);
  const buitenBudget = budgetRollup ? budgetRollup.over + budgetRollup.under : null;
  const weekstartItems = [
    {
      key: 'krappe', icon: AlertTriangle, label: 'Krappe locaties',
      value: krappe, alert: krappe > 0,
      sublabel: `${k.red_locations || 0} rood · ${k.yellow_locations || 0} geel`,
      to: '/locaties',
    },
    {
      key: 'goedkeuringen', icon: ClipboardList, label: 'Openstaande goedkeuringen',
      value: data.approvals.total, alert: data.approvals.total > 0,
      sublabel: `${data.approvals.past_planned_unapproved} uren · ${data.approvals.time_records_needs_review} klok`,
      to: '/goedkeuringen?loc=all',
    },
    {
      key: 'sollicitaties', icon: Briefcase, label: 'Nieuwe sollicitaties',
      value: funnel ? funnel.nieuwe_sollicitaties : '—', alert: !!(funnel && funnel.nieuwe_sollicitaties > 0),
      sublabel: 'Te beoordelen',
      to: `/vacatures?loc=${scope}`,
    },
    {
      key: 'budget', icon: Activity, label: 'Locaties buiten budget',
      value: buitenBudget == null ? '—' : buitenBudget, alert: !!(buitenBudget && buitenBudget > 0),
      sublabel: budgetRollup ? `${budgetRollup.over} boven · ${budgetRollup.under} onder` : 'Laden…',
      to: `/rooster?loc=${budgetRollup?.firstSlug || 'all'}`,
    },
  ];

  return (
    <div className="space-y-6" data-testid="page-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{greeting}, {firstNameCap}</h2>
          <p className="mt-1 text-sm text-slate-500">
            Week {data.iso_week} / {data.iso_year} — overzicht van staffing, openstaande acties en AI-inzichten.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" data-testid="dashboard-refresh" onClick={() => load(false)} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'animate-spin mr-1.5' : 'mr-1.5'} />
          Verversen
        </Button>
      </div>

      {/* Weekstart — structured at-a-glance (separate from the AI narrative below) */}
      <WeekstartWidget items={weekstartItems} />

      <ContractSignaleringWidget
        data={contractSig && globalLocation !== 'all'
          ? {
            ...contractSig,
            per_location: contractSig.per_location.filter((r) => r.location_slug === globalLocation),
            verlopen: contractSig.per_location.filter((r) => r.location_slug === globalLocation)
              .reduce((s, r) => s + r.verlopen, 0),
            bijna: contractSig.per_location.filter((r) => r.location_slug === globalLocation)
              .reduce((s, r) => s + r.bijna, 0),
          }
          : contractSig}
      />

      {/* AI Weekbriefing */}
      {data.briefing && (
        <div
          className="relative overflow-hidden rounded-lg border border-[#e4e9f0] bg-white p-5 shadow-sm border-l-[3px] border-l-tw1-accent-gold"
          data-testid="ai-weekbriefing"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="flex h-[38px] w-[38px] items-center justify-center rounded-lg bg-[#fdf3da] text-[#9a6b16]">
                <Sparkles size={18} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-tw1-brand-navy">AI Weekbriefing</h3>
                <p className="text-[10px] text-tw1-text-muted">
                  {data.briefing.from_cache ? `Gegenereerd ${new Date(data.briefing.generated_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })} · Cache 6u` : 'Net gegenereerd'} · {data.briefing.usage?.model || 'fallback'}
                </p>
              </div>
            </div>
            {isOwnerAdmin && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={regenerateBriefing}
                disabled={briefingBusy}
                data-testid="briefing-regenerate"
                className="text-brand-navy hover:bg-brand-navy/5 h-8"
              >
                {briefingBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                Nieuwe briefing
              </Button>
            )}
          </div>
          <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-line">{data.briefing.briefing_text}</p>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <KpiCard
          icon={Building2}
          label="Locaties"
          value={k.locations_count}
          sublabel={`${k.truckwash_count} truckwash · ${k.organisatorisch_count} overig`}
          type="neutral"
        />
        <KpiCard
          icon={Users}
          label="Medewerkers"
          value={k.employees_active}
          sublabel="in dienst"
          type="neutral"
          to="/medewerkers?loc=all"
        />
        <KpiCard
          icon={AlertCircle}
          label="Openstaande acties"
          value={data.approvals.total}
          sublabel={`${data.approvals.past_planned_unapproved} goedkeuren · ${data.approvals.time_records_needs_review} klok-review`}
          type={data.approvals.total > 0 ? 'attention' : 'neutral'}
          to={`/goedkeuringen?loc=all`}
        />
        <KpiCard
          icon={Activity}
          label="Krappe locaties"
          value={k.red_locations + k.yellow_locations}
          sublabel={`${k.red_locations} rood · ${k.yellow_locations} geel`}
          type={k.red_locations > 0 ? 'danger' : k.yellow_locations > 0 ? 'attention' : 'neutral'}
          onClick={() => setFilter(k.red_locations > 0 ? 'red' : 'yellow')}
        />
        <KpiCard
          icon={FileText}
          label="Documenten attentie"
          value={data.documents?.count || 0}
          sublabel={(() => {
            const d = data.documents?.buckets || { expired: [], red: [], orange: [] };
            const ex = d.expired?.length || 0;
            const rd = d.red?.length || 0;
            const og = d.orange?.length || 0;
            if (!data.documents?.count) return 'Alles in orde';
            return `${ex} verlopen · ${rd} < 7d · ${og} < 30d`;
          })()}
          type={(() => {
            const d = data.documents?.buckets || { expired: [], red: [], orange: [] };
            const urgent = (d.expired?.length || 0) + (d.red?.length || 0);
            if (urgent > 0) return 'danger';
            if ((d.orange?.length || 0) > 0) return 'attention';
            return 'neutral';
          })()}
          to="/instellingen?tab=documenten&loc=all"
        />
        <KpiCard
          icon={Activity}
          label="Uren-budget"
          value={budgetRollup ? budgetRollup.over + budgetRollup.under : '—'}
          sublabel={budgetRollup ? `${budgetRollup.over} boven · ${budgetRollup.under} onder` : 'Laden…'}
          type={budgetRollup && budgetRollup.over > 0 ? 'danger' : budgetRollup && budgetRollup.under > 0 ? 'attention' : 'neutral'}
          to={`/rooster?loc=${budgetRollup?.firstSlug || 'all'}`}
        />
      </div>

      {/* Staffing per locatie */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Staffing per locatie</h3>
            <p className="text-[11px] text-slate-500">Klik op een tegel om naar Roosterplanner te gaan.</p>
          </div>
          <div className="inline-flex rounded-md border border-slate-200 bg-white text-xs" data-testid="staffing-filter">
            {['all', 'red', 'yellow', 'green'].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                data-testid={`staffing-filter-${f}`}
                className={`px-3 py-1.5 border-r last:border-r-0 border-slate-200 transition-colors ${
                  filter === f ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {f === 'all' ? 'Alle' : STATUS_META[f].label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="staffing-grid">
          {filteredStaffing.length === 0 ? (
            <div className="col-span-full rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              {filter === 'all' ? 'Geen locaties met activiteit deze week.' : `Geen ${STATUS_META[filter]?.label.toLowerCase()} locaties.`}
            </div>
          ) : (
            filteredStaffing.map((s) => <StaffingTile key={s.slug} loc={s} />)
          )}
        </div>
      </div>

      {/* Documenten attentiepunten widget */}
      <DocumentenWidget docs={data.documents} />

      {/* Werving / recruitment funnel */}
      <WervingWidget funnel={funnel} scope={globalLocation && globalLocation !== 'all' ? globalLocation : 'all'} />

      {/* Loonkosten deze week — owner/admin all-locations rollup + PK% */}
      {isOwnerAdmin && <LoonkostenWeekWidget rollup={costRollup} week={data.iso_week} />}

      {/* Datakwaliteit — owner/admin integrity check (orphaned location refs) */}
      {isOwnerAdmin && <DataHealthWidget health={dataHealth} />}

      {/* Quick actions */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Link to={`/goedkeuringen?loc=all`} data-testid="quick-action-uren" className="rounded-lg border border-slate-200 bg-white p-4 hover:shadow-md hover:border-brand-amber transition-all flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-slate-900">Uren goedkeuren</p>
            <p className="text-[11px] text-slate-500">{data.approvals.total} openstaand</p>
          </div>
          <ClipboardList size={20} className="text-brand-amber-dark" />
        </Link>
        <Link to={`/verloning?loc=${primaryTarget}`} data-testid="quick-action-verloning" className="rounded-lg border border-slate-200 bg-white p-4 hover:shadow-md hover:border-brand-amber transition-all flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-slate-900">Naar Verloning</p>
            <p className="text-[11px] text-slate-500">Periode {data.iso_week}/{data.iso_year}</p>
          </div>
          <Briefcase size={20} className="text-brand-amber-dark" />
        </Link>
        <Link to={`/weekoverzicht?loc=${primaryTarget}`} data-testid="quick-action-week" className="rounded-lg border border-slate-200 bg-white p-4 hover:shadow-md hover:border-brand-amber transition-all flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-slate-900">Weekoverzicht</p>
            <p className="text-[11px] text-slate-500">Retrospectief detail</p>
          </div>
          <Calendar size={20} className="text-brand-amber-dark" />
        </Link>
      </div>
    </div>
  );
}
